import { useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/apiService';
import CompetitionFilter, { ALL_COMPETITIONS, sortCompetitions } from '../../helpers/CompetitionFilter';
import PredictionAccuracyCard from './PredictionAccuracyCard';
import PredictionResultsCard, { gradeResults } from './PredictionResultsCard';
import Pagination from '../../templates/Pagination';


const ACCURACY_MARKET = 'exact_score';

// The box opens on whichever league we called best over this window.
const BEST_LEAGUE_WINDOW_DAYS = 14;

// The All chip mixes every league's latest results (~25 rows), so the list pages
// rather than running the length of the page.
const RESULTS_PAGE_SIZE = 5;

const isHit = (status) => status === 'EXACT' || status === 'RESULT';

// Highest share of correct outcomes (EXACT or RESULT) over the last two weeks, ties
// broken by exact scorelines, then sample size, then the shared chip order. If no
// league has played in that window (international break, off-season) it ranks on the
// latest results we have instead, so there's always a default.
const pickBestCompetition = (graded, competitions) => {
    const cutoff = Date.now() - BEST_LEAGUE_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    const recent = graded.filter(({ row }) => new Date(row.match_date).getTime() >= cutoff);
    const pool = recent.length > 0 ? recent : graded;

    const tally = new Map();
    pool.forEach(({ row, status }) => {
        const id = String(row.competition?.id);
        const entry = tally.get(id) ?? { games: 0, hits: 0, exact: 0 };
        entry.games += 1;
        entry.hits += isHit(status) ? 1 : 0;
        entry.exact += status === 'EXACT' ? 1 : 0;
        tally.set(id, entry);
    });

    const order = competitions.map((comp) => String(comp.id));
    const ranked = [...tally.entries()].sort(([idA, a], [idB, b]) => (
        b.hits / b.games - a.hits / a.games
        || b.exact - a.exact
        || b.games - a.games
        || order.indexOf(idA) - order.indexOf(idB)
    ));

    return ranked[0]?.[0] ?? ALL_COMPETITIONS;
};

// "Proof it works" scorecard on the logged-out home: its own league filter (with an
// All chip) over the accuracy summary and the latest graded results. It's independent
// of the hero's league filter, which follows upcoming fixtures rather than results.
const TrackRecord = () => {
    // Both feeds return every league in one call, so switching chips never goes back
    // to the network. null = still loading.
    const [results, setResults] = useState(null);
    const [accuracy, setAccuracy] = useState(null);
    const [selected, setSelected] = useState(null);
    const [page, setPage] = useState(1);
    const requested = useRef(false);

    useEffect(() => {
        if (requested.current) return;
        requested.current = true;

        const base = import.meta.env.VITE_API_URL;
        // Secondary content on a marketing page: a failed request resolves to "nothing
        // to show" (the cards drop out) rather than surfacing an error to every visitor.
        apiService.apiGet(`${base}/public/benchmarks/match-results`).then(
            (response) => setResults(Array.isArray(response?.data) ? response.data : []),
            () => setResults([])
        );
        apiService.apiGet(`${base}/public/benchmarks/accuracy?market=${ACCURACY_MARKET}`).then(
            (response) => setAccuracy(Array.isArray(response?.data) ? response.data : []),
            () => setAccuracy([])
        );
    }, []);

    const graded = useMemo(() => (results === null ? null : gradeResults(results)), [results]);

    // Only leagues with something graded get a chip, so a chip never opens an empty box.
    const competitions = useMemo(() => {
        const map = new Map();
        (graded ?? []).forEach(({ row }) => {
            const comp = row.competition;
            if (comp?.id != null && !map.has(comp.id)) map.set(comp.id, { id: comp.id, season_name: comp.name });
        });
        return sortCompetitions(Array.from(map.values()));
    }, [graded]);

    // Resolve the default once, so it never overrides a chip the visitor picked.
    useEffect(() => {
        if (selected !== null || graded === null) return;
        setSelected(pickBestCompetition(graded, competitions));
    }, [graded, competitions, selected]);

    const entries = useMemo(() => {
        if (graded === null || selected === null) return null;
        if (selected === ALL_COMPETITIONS) return graded;
        return graded.filter(({ row }) => String(row.competition?.id) === String(selected));
    }, [graded, selected]);

    // A new chip starts back on its first page.
    const selectCompetition = (id) => {
        setSelected(id);
        setPage(1);
    };

    const pagesTotal = entries ? Math.ceil(entries.length / RESULTS_PAGE_SIZE) : 0;
    const pageEntries = entries && entries.slice((page - 1) * RESULTS_PAGE_SIZE, page * RESULTS_PAGE_SIZE);

    // Pagination hands back the clicked number as text and Previous/Next as page ± 1,
    // so coerce and clamp before using it.
    const goToPage = (next) => {
        const target = Number(next);
        if (Number.isFinite(target) && target >= 1 && target <= pagesTotal) setPage(target);
    };

    return (
        <>
            {competitions.length > 1 && (
                <div className="mb-3 w-100">
                    <CompetitionFilter
                        id="track-record-filter"
                        competitions={competitions}
                        value={selected}
                        onChange={selectCompetition}
                    />
                </div>
            )}
            <PredictionAccuracyCard rows={accuracy} competitionId={selected} className="mb-3" hideScore />
            <PredictionResultsCard entries={pageEntries} />
            {pagesTotal > 1 && (
                <div className="mt-3">
                    <Pagination page={page} pages_total={pagesTotal} onItemClick={goToPage} />
                </div>
            )}
        </>
    );
};

export default TrackRecord;
